import type { BirthdayVisibilityEnum, GenderEnum } from 'types/generated/client';
import { BIRTHDAY_VISIBILITY_OPTIONS, GENDER_OPTIONS } from 'utils/shared/playerFinder';
import ChevronDown from 'svg/ChevronDown';
import FormTextInput from 'components/Find/FormTextInput';
import classNames from 'styles/utils/classNames';

/**
 * The "about you" inputs, shared by the Find onboarding step and the profile's
 * About Me editor. Each is a controlled field so react-hook-form can wrap it in a
 * Controller on one surface while the other drives it from local state.
 */

export function AboutMeInput({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder="Help players get to know you"
      className="input-unstyled typography-product-body w-full resize-none rounded-md border-none bg-color-bg-input-lightmode-primary px-ds-md py-ds-sm text-color-text-lightmode-primary outline-none ring-transparent placeholder:text-color-text-lightmode-placeholder focus:outline-none dark:bg-color-bg-input-darkmode-primary dark:text-color-text-darkmode-primary"
    />
  );
}

export function GenderField({
  name,
  value,
  onChange,
}: {
  /** Groups the radios; two instances on one page must not share a name. */
  name: string;
  value: GenderEnum | null;
  onChange: (value: GenderEnum) => void;
}) {
  return (
    <div className="flex flex-wrap gap-ds-lg">
      {GENDER_OPTIONS.map((option) => (
        <label key={option.value} className="flex cursor-pointer items-center gap-ds-sm">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="h-5 w-5 shrink-0 border-color-border-input-lightmode text-color-checkbox-active focus:ring-color-text-lightmode-primary dark:border-color-border-input-darkmode"
          />
          <span className="typography-product-body text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
            {option.label}
          </span>
        </label>
      ))}
    </div>
  );
}

export function BirthdayField({
  birthday,
  onBirthdayChange,
  visibility,
  onVisibilityChange,
}: {
  birthday: string;
  onBirthdayChange: (value: string) => void;
  visibility: BirthdayVisibilityEnum;
  onVisibilityChange: (value: BirthdayVisibilityEnum) => void;
}) {
  return (
    <div className="flex items-start gap-ds-lg">
      <div className="flex-1">
        <FormTextInput
          type="date"
          aria-label="Birthday"
          value={birthday}
          onChange={(event) => onBirthdayChange(event.target.value)}
          className={classNames(
            !birthday &&
              '!text-color-text-lightmode-placeholder dark:!text-color-text-darkmode-placeholder',
          )}
        />
      </div>

      <div className="relative flex-1">
        <select
          aria-label="Who can see your birthday"
          value={visibility}
          onChange={(event) => onVisibilityChange(event.target.value as BirthdayVisibilityEnum)}
          className="input-unstyled typography-product-body h-[2.625rem] w-full appearance-none rounded-lg border-none bg-color-bg-input-lightmode-primary px-ds-md pr-9 text-color-text-lightmode-primary outline-none ring-transparent focus:outline-none dark:bg-color-bg-input-darkmode-primary dark:text-color-text-darkmode-primary"
        >
          {BIRTHDAY_VISIBILITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-ds-md top-1/2 h-5 w-5 -translate-y-1/2 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
      </div>
    </div>
  );
}
